<template>
  <div class="timeline-table">
    <div class="timeline-table__header">
      <div class="timeline-table__tabs-wrapper">
        <TimelineTabs
          :activeTab="activeTab"
          :isLoading="isLoading"
          @update:activeTab="activeTab = $event"
        />
      </div>
      <div class="timeline-table__spacer" />
      <div class="timeline-table__collapse-btn">
        <McButton
          v-if="allVehiclesHaveFleetTask"
          v-show="activeTab === 'UNSERVED'"
          v-b-tooltip.hover.html.bottomright="{
            title: !hasUserGroup
              ? $t('routeOptimization.tooltip.noUserGroup.createNewRO')
              : $t('routeOptimization.tooltip.selectDOFirst'),
            disabled: !hasUserGroup || selectedUnservedIdsLength > 0,
          }"
          variant="secondary"
          size="sm"
          icon="icon-fill_plus"
          :text="$t('routeOptimization.action.createNew')"
          :disabled="selectedUnservedIdsLength === 0"
          @click="createNewROFromUnserved"
        />
        <McButton
          v-if="hasReset && !hasFOCreated"
          variant="secondary"
          size="sm"
          icon="icon-fill_refresh"
          :text="$t('global.resetAsset')"
          @click="handleReset()"
        />
        <McButton
          variant="secondary"
          size="sm"
          :icon="isShowMap ? 'icon-outline_expand' : 'icon-outline_collapse'"
          :text="isShowMap ? 'Expand' : 'Collapse'"
          @click="handleToggleExpand(!isShowMap)"
        />
      </div>
    </div>

    <!-- Tab Content -->
    <ServedContent
      v-if="activeTab === 'SERVED'"
      :isShowMap="isShowMap"
      :isCreatingFO="isCreatingFO"
      :showResponse="showResponse"
      :selected="selected"
      :selectedAll="selectedAll"
      :selectedIdsLength="selectedIdsLength"
      :timeCols="timeCols"
      :totalMinutes="totalMinutes"
      :hasFOCreated="hasFOCreated"
      :isPending="isPending"
      :isRoSwatActive="isRoSwatActive"
      :routeOptimizationId="routeOptimizationId"
      :settings="settings"
      @selectAll="handleSelectAll"
      @toggleShowAll="handleToggleShowAll"
      @toggleShowRoute="handleToggleShowRoute"
      @selectVehicle="handleSelectVehicle"
      @dragStart="onDragStart"
      @dragEnd="onDragEnd"
      @drop="onDrop"
      @dropToTimeline="onDropToTimeline"
      @unselectAll="handleUnselectAll"
      @assignVehicle="(payload) => $emit('assignVehicle', payload)"
      @hasBeenAddedToTrip="(roId, data, res) => $emit('hasBeenAddedToTrip', roId, data, res)"
    />

    <UnservedContent
      v-show="activeTab === 'UNSERVED'"
      ref="unservedContent"
      :vehicles="showResponse?.vehicles"
      :isPending="isPending"
      @selectedItemsChange="handleSelectedUnservedItemsChange"
    />

    <ChangeSequence
      :from="dragData"
      :to="dropData"
      :position="dropPosition"
      :sameVehicle="hasSameVehicle"
      :settings="settings"
      :addedLast="addedLast"
      :timeWindows="hasTimeWindows"
      :isRoSwatActive="isRoSwatActive"
      @quotaStatus="handleQuotaStatus"
    />
    <ResetTimeline />
    <RODeliveryWarningModal
      @close="handleWarningModalClose"
      @viewDetails="handleWarningViewDetails"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import McButton from '@/components/McButton'

import TimelineTabs from './components/Tabs'
import ServedContent from './content/Served'
import UnservedContent from './content/Unserved'
import ChangeSequence from './components/ChangeSequence'
import ResetTimeline from './components/ResetTimeline'
import RODeliveryWarningModal from './components/RODeliveryWarningModal'

import timeCols from '../../lib/constants/time-columns.js'
import { EventBus } from '../../lib/event-bus.js'

import moment from 'moment'

export default {
  components: {
    McButton,
    TimelineTabs,
    ServedContent,
    UnservedContent,
    ChangeSequence,
    ResetTimeline,
    RODeliveryWarningModal,
  },
  mixins: [Helper],
  props: {
    isShowMap: {
      type: Boolean,
      default: true,
    },
    isCreatingFO: {
      type: Boolean,
      default: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    parentHeight: {
      type: Number,
      default: 0,
    },
    showResponse: {
      type: Object,
      default: () => {},
    },
    selected: {
      type: Object,
      default: () => {},
    },
    settings: {
      type: Object,
      default: () => {},
    },
    routeOptimizationId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: [
    'update:isShowMap',
    'update:selected',
    'quotaStatus',
    'createNewRO',
    'selectedUnservedItemsChange',
    'unservedShowResponse',
    'assignVehicle',
    'hasBeenAddedToTrip',
  ],
  data() {
    return {
      timeCols: timeCols,
      containerHeightPercent: 0.25,
      selectedAll: true,
      selectedUnservedItems: [],

      dragData: null,
      dropData: null,
      dropPosition: null,
      hasSameVehicle: false,
      addedLast: false,
      hasTimeWindows: false,

      activeTab: 'SERVED',
      isPending: false,
    }
  },
  computed: {
    ...mapState('routeOptimization', ['showedRoutes', 'unservedShowResponse']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
      getFeatureStrategies: 'identity/getFeatureStrategies',
    }),
    hasTmsUserGroupScope() {
      return this.hasAccessAll(['tms-user-group:read'])
    },
    vehicles() {
      return this.showResponse.vehicles || []
    },
    totalMinutes() {
      let startTime = moment(this.showResponse.expected_departure_on).seconds(0)
      let endTime = moment(this.setEndTime())

      const startMinute = startTime.minutes()
      const endMinute = endTime.minutes()

      if (startMinute > endMinute) {
        endTime = endTime.add(1, 'h')
      }

      let totalMinutes = 0
      const isTrue = true

      while (isTrue) {
        if (startTime.isAfter(endTime)) break

        totalMinutes += 60
        startTime.add(1, 'h')
      }

      return totalMinutes
    },
    iconToggleShowAll() {
      const isAllShowed = Object.keys(this.showedRoutes).every((key) => {
        return this.showedRoutes[key]
      })

      if (isAllShowed) {
        return 'icon-fill_eye'
      }

      const isAllNotShowed = Object.keys(this.showedRoutes).every((key) => {
        return this.showedRoutes[key] === false
      })

      if (isAllNotShowed) {
        return 'icon-fill_eye-off'
      }

      return 'icon-fill_eye'
    },
    disableSelectAll() {
      return this.showResponse?.vehicles?.some((vehicle) => vehicle.fleet_task_id !== null)
    },
    vehicleErrors() {
      const vehicles = {}

      this.showResponse.vehicles.forEach((vehicle) => {
        if (!vehicle.is_error) return
        if (vehicle.license_plate !== 'Data Telah Dihapus') return

        vehicles[vehicle.id] = true
      })

      return vehicles
    },
    selectedIdsLength() {
      return Object.values(this.selected).filter(Boolean).length
    },
    selectedUnservedIdsLength() {
      return this.selectedUnservedItems.length
    },
    hasReset() {
      return this.showResponse?.reset_id
    },
    hasFOCreated() {
      if (
        !this.showResponse?.status ||
        this.showResponse?.status.raw_type !== 'SUCCESS' ||
        !this.showResponse.vehicles ||
        !Array.isArray(this.showResponse.vehicles)
      )
        return false
      return this.showResponse.vehicles.some((vehicle) => vehicle.fleet_task_id !== null)
    },
    allVehiclesHaveFleetTask() {
      return (
        (this.showResponse?.vehicles?.every((vehicle) => vehicle.fleet_task_id !== null) &&
          this.unservedShowResponse?.metadata?.total_served > 0) ||
        false
      )
    },
    isRoSwatActive() {
      const isSamator =
        this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive =
        this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false

      return isSamator && isActive
    },
    hasUserGroup() {
      return this.hasTmsUserGroupScope && this.showResponse?.user_group != null
    },
  },
  watch: {
    showResponse: {
      handler(newValue, oldValue) {
        if (newValue?.status?.raw_type === 'PENDING') {
          this.isPending = true
        }

        if (newValue?.status?.raw_type == oldValue?.status?.raw_type) return

        this.initTable(newValue?.status?.raw_type)

        const hasAnyFleetTask = newValue?.vehicles?.some(
          (vehicle) => vehicle.fleet_task_id !== null
        )
        if (hasAnyFleetTask && this.selectedUnservedItems.length > 0) {
          this.selectedUnservedItems = []
        }
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    EventBus.$on('set-pending-status', this.handleSetPendingStatus)
  },
  mounted() {
    this.initTable(this.showResponse?.status?.raw_type)
  },
  beforeDestroy() {
    EventBus.$off('set-pending-status', this.handleSetPendingStatus)
  },
  methods: {
    initTable(status) {
      if (!status) return
      if (status === 'PENDING') return

      this.setColsByTime()
      this.setSelected(true, 'initial')
    },

    setEndTime() {
      let result = moment(this.showResponse.expected_departure_on)

      this.vehicles.forEach((vehicle) => {
        const lastPointIdx = vehicle.timelines.length - 1
        const lastPointDepartTime =
          vehicle.timelines[lastPointIdx].type === 'END'
            ? vehicle.timelines[lastPointIdx].estimated_process_on
            : vehicle.timelines[lastPointIdx].estimated_departure_on

        if (result.isSameOrAfter(lastPointDepartTime, 'hours')) return

        result = moment(lastPointDepartTime).seconds(0)
      })

      return result
    },

    setColsByTime() {
      if (Object.keys(this.showResponse).length == 0) return

      const timeCols = []
      let startTime = moment(this.showResponse.expected_departure_on).seconds(0)
      let endTime = moment(this.setEndTime())

      const startMinute = startTime.minutes()
      const endMinute = endTime.minutes()

      if (startMinute > endMinute) {
        endTime = endTime.add(1, 'h')
      }

      let hour = startTime.hours()
      let day = 0
      const isTrue = true

      while (isTrue) {
        if (startTime.isAfter(endTime)) break

        const hours = hour < 10 ? '0' + hour : hour
        let formattedTime = hours + ':00'

        if (day > 0) {
          formattedTime += ` (+${day})`
        }

        timeCols.push(formattedTime)
        startTime.add(1, 'h')

        if (hour == 23) {
          hour = 0
          day++
          continue
        }

        hour++
      }

      this.timeCols = timeCols
    },

    setSelected(value = true, type = 'initial') {
      const newSelected = {}
      let hasDisabledUnselected = false

      this.vehicles.forEach((vehicle) => {
        // Jika vehicle disabled, unselect
        if (vehicle.fleet_task_id || this.isPending || this.isCreatingFO) {
          newSelected[vehicle.id] = false
          hasDisabledUnselected = true
          return
        }
        if (type !== 'initial' && vehicle.fleet_task_id) {
          newSelected[vehicle.id] = this.selected[vehicle.id]
          return
        }
        newSelected[vehicle.id] = value
      })

      this.$emit('update:selected', newSelected)

      // selectedAll harus false jika ada yang disabled dan unselect
      if (hasDisabledUnselected) {
        this.selectedAll = false
      } else {
        const allSelected = this.vehicles.every((v) => newSelected[v.id])
        this.selectedAll = allSelected
      }
    },

    handleToggleExpand(value) {
      if (value) {
        this.containerHeightPercent = 0.25
      } else {
        this.containerHeightPercent = 1
      }

      this.$emit('update:isShowMap', value)
    },

    handleSelectAll(value) {
      this.setSelected(value, 'select-all')
    },

    handleToggleShowAll() {
      let toggleValue = true
      const isAllRoutesShowed = Object.keys(this.showedRoutes).every((key) => {
        return this.showedRoutes[key]
      })

      if (isAllRoutesShowed) {
        toggleValue = false
      }

      this.$store.dispatch('routeOptimization/toggleAllShowedRoutes', toggleValue)
    },

    handleToggleShowRoute(vehicle) {
      this.$store.dispatch('routeOptimization/setShowedRoutes', {
        key: vehicle.id,
        value: !this.showedRoutes[vehicle.id],
      })
    },

    isSolutionError({ vehicle }) {
      // if is_error in vehicle is false, the vehicle has no error at all
      if (!vehicle.is_error) {
        return false
      }

      // set error delivery order
      let result = false
      for (const point of vehicle.timelines) {
        for (const activity of point.activities) {
          if (!activity.delivery_order.is_error) continue

          result = true
          break
        }

        if (result) break
      }

      return result
    },

    getFilteredTimelines(timelines) {
      const includedTypes = this.isRoSwatActive
        ? ['BREAK', 'IDLE', 'END']
        : ['BREAK', 'IDLE', 'END']
      const hiddenPointId = this.getStartPointId(timelines)

      return timelines.filter((point) => {
        if (point.point_id === hiddenPointId) return false
        if (includedTypes.includes(point.type)) return true
        if (this.isStartPickupDefaultPoint(timelines, point)) return true
        if (point.type === 'DEFAULT' && point.service_time !== 'PT0S') {
          return true
        }
        return false
      })
    },

    getStartPointId(timelines) {
      if (!this.isRoSwatActive) return null

      return timelines.find((point) => point.type === 'START')?.point_id || null
    },

    getStartPickupDefaultPointId(timelines) {
      if (!this.isRoSwatActive) return null

      const startPoint = timelines.find((point) => point.type === 'START')
      const pickupDefaultPoint = timelines.find((point) => {
        if (point.type !== 'DEFAULT') return false

        const hasPickupActivity = point.activities?.some((activity) => activity.type === 'PICKUP')
        if (!hasPickupActivity) return false

        const startAddressId = startPoint?.address?.id
        const pointAddressId = point.address?.id

        if (startAddressId != null && pointAddressId != null) {
          return startAddressId === pointAddressId
        }

        return startPoint?.address?.name === point.address?.name
      })

      if (!startPoint || !pickupDefaultPoint) return null

      return pickupDefaultPoint.point_id
    },

    isStartPickupDefaultPoint(timelines, point) {
      return point.point_id === this.getStartPickupDefaultPointId(timelines)
    },

    getPointIndex(timelines, currentPoint) {
      const excludedTypes = ['BREAK', 'IDLE', 'END']
      if (
        excludedTypes.includes(currentPoint.type) ||
        this.isStartPickupDefaultPoint(timelines, currentPoint)
      ) {
        return null
      }

      let index = 0
      const hiddenPointId = this.getStartPointId(timelines)
      for (const point of timelines) {
        if (point.point_id === hiddenPointId) continue
        if (this.isStartPickupDefaultPoint(timelines, point)) continue

        if (point.type === 'DEFAULT' && point.service_time !== 'PT0S') {
          index++
          if (point.point_id === currentPoint.point_id) {
            return index
          }
        }
      }
      return index
    },
    getSequenceIndexFromPoint(timelines, point) {
      if (!point) return null

      const pointIndex = this.getPointIndex(timelines, point)
      if (typeof pointIndex !== 'number' || pointIndex <= 0) return null

      return pointIndex
    },
    getLastSequenceIndex(timelines) {
      let lastIndex = 0
      const hiddenPointId = this.getStartPointId(timelines)

      for (const point of timelines) {
        if (point.point_id === hiddenPointId) continue
        if (this.isStartPickupDefaultPoint(timelines, point)) continue

        if (point.type === 'DEFAULT' && point.service_time !== 'PT0S') {
          lastIndex++
        }
      }

      return lastIndex > 0 ? lastIndex : null
    },
    hasActivityType(point, type) {
      const normalizedType = String(type || '').toUpperCase()

      return point?.activities?.some(
        (activity) => String(activity?.type || '').toUpperCase() === normalizedType
      )
    },
    getNextDefaultDropPointAfter(timelines, targetPoint) {
      const targetTimelineIndex = timelines.findIndex(
        (point) => point.point_id === targetPoint?.point_id
      )
      if (targetTimelineIndex < 0) return null

      for (let i = targetTimelineIndex + 1; i < timelines.length; i++) {
        const point = timelines[i]
        if (point.type !== 'DEFAULT') continue
        if (!this.hasActivityType(point, 'DROP')) continue

        return point
      }

      return null
    },
    normalizeDropTargetFromPickup(timelines, targetPoint, targetIndex) {
      if (!targetPoint || targetPoint.type !== 'DEFAULT') {
        return {
          targetPoint,
          targetIndex,
          forceBefore: false,
        }
      }

      if (!this.hasActivityType(targetPoint, 'PICKUP')) {
        return {
          targetPoint,
          targetIndex,
          forceBefore: false,
        }
      }

      const nextDropPoint = this.getNextDefaultDropPointAfter(timelines, targetPoint)
      if (!nextDropPoint) {
        return {
          targetPoint,
          targetIndex,
          forceBefore: false,
        }
      }

      return {
        targetPoint: nextDropPoint,
        targetIndex: this.getPointIndex(timelines, nextDropPoint),
        forceBefore: true,
      }
    },
    handleSelectVehicle(value, vehicle) {
      const newSelected = { ...this.selected }
      newSelected[vehicle.id] = value
      this.$emit('update:selected', newSelected)

      const allSelected = this.vehicles.every((v) => newSelected[v.id])
      this.selectedAll = allSelected
    },
    onDragStart(point, vehicle, index) {
      this.addedLast = false
      this.hasTimeWindows = false
      if (point.type !== 'DEFAULT') return

      if (point.time_windows) {
        this.hasTimeWindows = true
      }

      this.dragData = { point, vehicle, index }
    },
    onDragEnd() {
      // no-op: kept for drag-end emit compatibility
    },
    onDrop(targetPoint, targetVehicle, targetIndex, event) {
      event.stopPropagation()
      if (targetPoint.type !== 'DEFAULT') return
      const timelines = targetVehicle?.timelines || []
      const normalizedDropTarget = this.normalizeDropTargetFromPickup(
        timelines,
        targetPoint,
        targetIndex
      )
      const normalizedTargetPoint = normalizedDropTarget.targetPoint
      const normalizedTargetIndex = normalizedDropTarget.targetIndex

      this.dropPosition = 'AFTER'
      this.hasSameVehicle = this.dragData?.vehicle.id === targetVehicle.id

      if (normalizedDropTarget.forceBefore) {
        this.dropPosition = 'BEFORE'
      } else if (
        this.hasSameVehicle &&
        typeof this.dragData?.index === 'number' &&
        typeof normalizedTargetIndex === 'number' &&
        this.dragData.index > normalizedTargetIndex
      ) {
        this.dropPosition = 'BEFORE'
      }

      let displayTargetIndex = normalizedTargetIndex

      // For cross-vehicle drop on a point with AFTER movement,
      // show the resulting sequence index (anchor index + 1).
      if (
        !this.hasSameVehicle &&
        this.dropPosition === 'AFTER' &&
        typeof normalizedTargetIndex === 'number'
      ) {
        displayTargetIndex = normalizedTargetIndex + 1
      }

      this.dropData = {
        targetPoint: normalizedTargetPoint,
        targetVehicle,
        targetIndex: displayTargetIndex,
      }

      if (this.dragData && this.dragData.point.point_id !== normalizedTargetPoint.point_id) {
        this.$bvModal.show('modal-change-sequence')
      }
    },
    onDropToTimeline(event, vehicle) {
      if (vehicle.fleet_task_id) return
      this.hasSameVehicle = this.dragData?.vehicle.id === vehicle.id
      const timelines = vehicle?.timelines || []
      const timelineEl = event.currentTarget
      const boundingRect = timelineEl.getBoundingClientRect()
      const dropX = event.clientX - boundingRect.left
      const timelineWidth = boundingRect.width

      const totalMinutes = this.totalMinutes
      const dropMinute = (dropX / timelineWidth) * totalMinutes

      const dropTime = moment(this.showResponse.expected_departure_on)
        .add(dropMinute, 'minutes')
        .seconds(0)

      const defaultPoints = this.getFilteredTimelines(timelines)

      let previousPoint = null
      let nextPoint = null
      let previousIndex = null
      let nextIndex = null

      for (let i = 0; i < defaultPoints.length; i++) {
        const pointTime = moment(
          defaultPoints[i].estimated_departure_on || defaultPoints[i].estimated_process_on
        )

        if (dropTime.isBefore(pointTime)) {
          nextPoint = defaultPoints[i]
          previousPoint = defaultPoints[i - 1] || null
          previousIndex = this.getSequenceIndexFromPoint(timelines, previousPoint)
          nextIndex = this.getSequenceIndexFromPoint(timelines, nextPoint)

          if (nextIndex === null && previousIndex !== null) {
            nextIndex = previousIndex + 1
          }
          break
        }
      }

      if (!nextPoint && defaultPoints.length > 0) {
        previousPoint = defaultPoints[defaultPoints.length - 1]
        previousIndex = this.getSequenceIndexFromPoint(timelines, previousPoint)

        if (previousIndex === null) {
          previousIndex = this.getLastSequenceIndex(timelines)
        }

        nextPoint = null
        nextIndex = previousIndex !== null ? previousIndex + 1 : 1
      }

      if (nextPoint && (nextPoint.type === 'IDLE' || nextPoint.type === 'BREAK')) {
        return
      }

      if (previousPoint && (previousPoint.type === 'IDLE' || previousPoint.type === 'BREAK')) {
        return
      }

      if (!previousPoint && nextPoint) {
        this.dropPosition = 'BEFORE'
      } else if ((previousPoint && nextPoint) || (!previousPoint && !nextPoint)) {
        this.dropPosition = 'AFTER'
      } else if (previousPoint && !nextPoint) {
        this.dropPosition = 'AFTER'
        nextIndex = previousIndex + 1
      }

      if (this.hasSameVehicle && this.dragData.index === nextIndex) {
        return
      }

      if (!nextPoint || nextPoint.type === 'END') {
        this.addedLast = true
      }

      const calculatedTargetPoint = this.dropPosition === 'AFTER' ? previousPoint : nextPoint
      const calculatedTargetIndex =
        this.dropPosition === 'AFTER' && this.hasSameVehicle
          ? previousIndex !== null && this.dragData.index > previousIndex
            ? nextIndex
            : previousIndex
          : nextIndex

      const normalizedDropTarget = this.normalizeDropTargetFromPickup(
        timelines,
        calculatedTargetPoint,
        calculatedTargetIndex
      )

      if (normalizedDropTarget.forceBefore) {
        this.dropPosition = 'BEFORE'
      }

      this.dropData = {
        targetPoint: normalizedDropTarget.targetPoint,
        targetVehicle: vehicle,
        targetIndex: normalizedDropTarget.targetIndex,
      }

      if (
        this.dragData &&
        this.dropData?.targetPoint &&
        this.dropData.targetPoint.type === 'DEFAULT' &&
        this.dragData.point.point_id !== this.dropData.targetPoint.point_id
      ) {
        this.$bvModal.show('modal-change-sequence')
      }
    },
    handleReset() {
      this.$bvModal.show('modal-reset-timeline')
    },

    handleQuotaStatus(status) {
      this.$emit('quotaStatus', status)
    },

    handleUnselectAll() {
      this.setSelected(false, 'unselect-all')
    },

    handleWarningModalClose() {
      this.activeTab = 'SERVED'
    },

    handleWarningViewDetails() {
      this.activeTab = 'UNSERVED'
    },

    handleSelectedUnservedItemsChange(selectedItems) {
      this.selectedUnservedItems = selectedItems
      this.$emit('selectedUnservedItemsChange', selectedItems)
    },

    createNewROFromUnserved() {
      const routeOptimizationId = this.$route.params.id

      const firstUnservedItem = this.$refs.unservedContent?.getFirstUnservedItem()
      if (!firstUnservedItem || !firstUnservedItem.origin_address?.id) {
        this.$mcToast.error(this.$t('global.alert.text.originAddressNotFound'))
        return
      }

      const originAddress = firstUnservedItem.origin_address

      const roData = {
        [routeOptimizationId]: {
          origin_address: {
            id: originAddress.id,
            name: originAddress.name,
          },
          selected_unserved: this.selectedUnservedItems,
          user_group:
            this.hasTmsUserGroupScope && this.isSuperUser()
              ? this.showResponse?.user_group
              : undefined,
        },
      }

      const existingData = JSON.parse(sessionStorage.getItem('createRouteOptimizeUnserved') || '[]')
      const existingIndex = existingData.findIndex((item) => item[routeOptimizationId])

      if (existingIndex !== -1) {
        existingData[existingIndex] = roData
      } else {
        existingData.push(roData)
      }

      sessionStorage.setItem('createRouteOptimizeUnserved', JSON.stringify(existingData))

      this.$router.push({
        name: 'Route Optimization Create',
        query: { ro_id: routeOptimizationId },
      })
    },

    handleSetPendingStatus(isPending) {
      this.isPending = isPending
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
